import {
    collection,
    collectionGroup,
    CollectionReference,
    doc,
    getDocs,
    query,
    updateDoc,
} from "firebase/firestore";
import FirebaseService from "./firebaseService";
import { Collection } from "../constants/Collection";
import { IDigitalDateCardRow } from "../types/DigitalCard";
import { DigitalCardReviewStatus } from "../constants/DigitalCardReviewStatus";
import { DigitalCardChangeCategory } from "../constants/DigitalCardChangeCategory";
import { NotificationType } from "../constants/NotificationType";
import { sendUserNotification } from "../utils/notificationUtils";

class DigitalDateCardService {
    ref: CollectionReference;

    constructor() {
        const db = FirebaseService.db;
        this.ref = collection(db, Collection.DATE_CARD_ORDERS);
    }

    private cardRef(uid: string, cardId: string) {
        return doc(this.ref, uid, Collection.DIGITAL_CARDS, cardId);
    }

    /**
     * DateCardOrders/{uid}/DigitalCards/{cardId} docs whose reviewStatus is unset or
     * 'pending-review'. Collection-group query across every user's DigitalCards
     * subcollection + client-side filter — same no-composite-index full-scan pattern
     * dateCardsService.ts already uses, just across a subcollection instead of the
     * top-level DateCardOrders docs (a user can now have more than one pending card).
     */
    async getPendingReviewDigitalCards(): Promise<IDigitalDateCardRow[]> {
        const docs = await getDocs(query(collectionGroup(FirebaseService.db, Collection.DIGITAL_CARDS)));
        const rows: IDigitalDateCardRow[] = [];

        docs.docs.forEach((cardDoc) => {
            const card = cardDoc.data() as Omit<IDigitalDateCardRow, "dateCardDocId" | "cardId">;
            const status = card.reviewStatus;
            if (status === undefined || status === DigitalCardReviewStatus.pendingReview) {
                const uid = cardDoc.ref.parent.parent?.id;
                if (!uid) return;
                rows.push({ ...card, dateCardDocId: uid, cardId: cardDoc.id });
            }
        });

        return rows;
    }

    /**
     * @param notifyUser Whether to push "your card is approved" to the user. Template-design
     * cards skip this — from the user's perspective a template was already approved design-wise,
     * so only custom-uploaded designs (which they're actually waiting to hear back on) notify.
     */
    async approve(uid: string, cardId: string, notifyUser: boolean = true): Promise<void> {
        await updateDoc(this.cardRef(uid, cardId), {
            reviewStatus: DigitalCardReviewStatus.approved,
            approvedAt: new Date().toISOString(),
        });

        if (!notifyUser) return;

        void sendUserNotification(uid, {
            title: 'Your card is approved 🎉',
            message: 'Your Date Card has been approved and is ready to use.',
            data: { type: NotificationType.DIGITAL_DATE_CARD_APPROVED, cardId },
        });
    }

    async requestChanges(
        uid: string,
        cardId: string,
        categories: DigitalCardChangeCategory[],
        adminNote?: string,
    ): Promise<void> {
        await updateDoc(this.cardRef(uid, cardId), {
            reviewStatus: DigitalCardReviewStatus.changesRequested,
            changeCategories: categories,
            adminNote: adminNote ?? '',
            changesRequestedAt: new Date().toISOString(),
        });

        void sendUserNotification(uid, {
            title: "We couldn't approve your Date Card",
            message: 'Open Skip to review what needs to be updated.',
            data: { type: NotificationType.DATE_CARD_CHANGES_REQUESTED, cardId },
        });
    }
}

export default new DigitalDateCardService();
